import { Spacing } from '@persona-id/design-tokens';
import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import { Fragment, useMemo, useState } from 'react';
import { Field, FormSection } from 'redux-form';
import styled from 'styled-components';

import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { FieldWrapper } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { FieldSelectOption } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/FormComponentFieldsEditor/FormComponentFieldsEditor';
import ReactSelectField from 'dashboard/components/FormControls/ReactSelectField';
import ToggleSliderField from 'dashboard/components/FormControls/ToggleSliderField';
import { OptionType } from 'dashboard/components/ReactSelect/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon/HelpIcon';
import Banner from 'design-system/components/Banner/Banner';
import { Label, Paragraph } from 'design-system/components/Text';
import { FieldSchema, FieldSchemaType } from 'lib/fields';
import { isRequired, withMessage } from 'lib/utils/formValidation';

const ToggleAndErrorContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: column;

  // begin rendering border after initial toggle row
  :not(:first-of-type) {
    border-top: 1px solid;
    border-color: ${({ theme }) => theme.color.border.secondary};
  }
`;

const ToggleContainer = styled('div')`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[4]} 0;
`;

const StyledDescription = styled(Paragraph).attrs(() => ({ size: 'md' }))`
  color: ${({ theme }) => theme.color.text.tertiary};
`;

const AlertContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing[4]} 0;
`;

const validate = {
  required: withMessage('Required', isRequired),
};

interface FormSectionAutoclassificationConfigProps {
  name: string;
  isAutoclassificationOn: boolean | undefined;
  setGovIdVtvAutoclassificationEnabledFeatures: (isAutoclassificationOn: boolean) => void;
  currentFieldKeyCountry: string | null;
  setFieldKeyCountry: (fieldKeyCountry: string | null) => void;
  prependCountrySelectStep: () => Promise<void>;
  fieldSchema: FieldSchema[];
}

const FormSectionAutoclassificationConfig = ({
  name,
  isAutoclassificationOn,
  setGovIdVtvAutoclassificationEnabledFeatures,
  currentFieldKeyCountry,
  setFieldKeyCountry,
  prependCountrySelectStep,
  fieldSchema,
}: FormSectionAutoclassificationConfigProps) => {
  const editableForFreeTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesFree,
  });

  const [isPrependingCountrySelectStep, setIsPrependingCountrySelectStep] = useState(false);

  const referencedFieldOptions = useMemo<OptionType[]>(() => {
    const unselectFieldOption = {
      label: 'No field',
      // We actually DO allow null as an option value, we explicitly handle it in front-end/design-system/components/Select/Select.tsx:307
      value: null as unknown as string,
    };
    const fieldOptions = fieldSchema
      .filter((field) => field.type === FieldSchemaType.String)
      .map(({ key, label }) => ({
        label: label,
        value: key,
      }));
    return [unselectFieldOption, ...fieldOptions];
  }, [fieldSchema]);

  const onAutoclassificationChange = (isAutoclassificationOn: boolean) => {
    setGovIdVtvAutoclassificationEnabledFeatures(isAutoclassificationOn);
    if (isAutoclassificationOn === true) {
      // if flipping autoclassification on, automatically set field key country to null
      setFieldKeyCountry(null);
    } else {
      // if flipping autoclassification off, set field key to selected_country_code if the field exists
      if (
        referencedFieldOptions.find(
          (fieldOptions) => fieldOptions.value === 'selected_country_code',
        )
      ) {
        setFieldKeyCountry('selected_country_code');
      }
    }
  };
  const onPrependCountryScreen = async () => {
    setIsPrependingCountrySelectStep(true);
    await prependCountrySelectStep();
    setIsPrependingCountrySelectStep(false);
  };

  return (
    <FormSection name={name} component={Fragment}>
      <ToggleAndErrorContainer>
        <ToggleContainer>
          <div>
            <Label size='md' weight='bold'>
              Autoclassification
            </Label>
            <StyledDescription>
              <span>
                Automatically determine the user&apos;s ID type without prompting them for it.{' '}
                <a
                  href='https://help.withpersona.com/articles/7kOzAY6feIPnzI3s5XbXVL/'
                  target='_blank'
                  rel='noreferrer'
                >
                  More details here
                </a>
              </span>
              <HelpIcon>
                <p>
                  With autoclassification on, the preceding country selection screen can be removed.
                </p>
              </HelpIcon>
            </StyledDescription>
          </div>
          <Field
            component={ToggleSliderField}
            name='auto-classification-config.is-enabled'
            id='auto-classification-config.is-enabled'
            labelOn='On'
            labelOff='Off'
            disabled={!editableForFreeTier}
            // This typing mismatch is coming from InputHTMLAttributes<HTMLInputElement> declaring onChange as
            // ChangeEventHandler<T> but ToggleSliderField declaring the type as (value: boolean) => void, and
            // we're passing down the onChange down to ToggleSliderField, but typescript is comparing against
            // the typing of ChangeEventHandler<T>, hence the 'as any' to prevent TSC errors
            onChange={onAutoclassificationChange as any}
          />
        </ToggleContainer>
      </ToggleAndErrorContainer>
      {!isAutoclassificationOn && currentFieldKeyCountry == null && (
        <Banner variant='alert' extendContentWidth={true}>
          <CircleAlertIcon />
          <AlertContainer>
            <div>Please choose a country field or add a country select screen before capture.</div>
            <a
              href='#'
              target='_blank'
              id='auto-classification-config__add-country-select-screen'
              rel='noreferrer'
              onClick={(e) => {
                e.preventDefault();
                if (!isPrependingCountrySelectStep) {
                  onPrependCountryScreen();
                }
              }}
              style={{
                textDecoration: 'underline',
                pointerEvents: isPrependingCountrySelectStep ? 'none' : 'auto',
                cursor: isPrependingCountrySelectStep ? 'default' : 'pointer',
              }}
            >
              Add country select screen
            </a>
          </AlertContainer>
        </Banner>
      )}
      <FieldWrapper>
        <Field
          component={ReactSelectField}
          options={referencedFieldOptions}
          label={
            <span>
              Country field key
              <HelpIcon>
                <p>
                  The field which the government ID step draws the country from, which determines
                  the ID types displayed to the user.
                </p>
                <br />
                <p>
                  If autoclassification is on, this is NOT recommended to be set unless the IDs MUST
                  be from a specific country specified by this field.
                </p>
              </HelpIcon>
            </span>
          }
          name='field-key-country'
          id='field-key-country'
          disabled={!editableForFreeTier}
          aria-label='Select country field'
          components={{ Option: FieldSelectOption }}
          validate={isAutoclassificationOn ? null : validate.required}
          placeholder='No field selected'
          menuPlacement='bottom'
          isSearchable
        />
      </FieldWrapper>
    </FormSection>
  );
};

export default FormSectionAutoclassificationConfig;
